import { useRef, useState } from "react";
import { Upload, FileJson, CheckCircle2, AlertTriangle, Loader2, X } from "lucide-react";
import { http, errText } from "@/lib/http";
import { toast } from "sonner";
import { Eyebrow } from "@/components/mh/bits";

/*
 * ImportDrop — drag-and-drop a Local Manahouse OS export into this workspace.
 * Flow: user drops .json → we read it → POST /api/mh/import/manahouse-os mode=dry_run
 * → preview the plan → user picks "merge" or "overwrite" → apply.
 * File never leaves the browser except as the import body. Secrets are not accepted.
 */

const Stat = ({ label, value, tone }) => (
  <div className={`rounded-xl border px-3 py-2 ${tone === "ok" ? "border-[#D4FF3A]/35 bg-[#D4FF3A]/[0.05]" : tone === "warn" ? "border-[#FFB020]/35 bg-[#FFB020]/[0.05]" : "border-white/10 bg-white/[0.03]"}`}>
    <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-display text-xl mt-0.5" style={{ color: tone === "ok" ? "#D4FF3A" : tone === "warn" ? "#FFB020" : undefined }}>{value}</div>
  </div>
);

export const ImportDrop = ({ reload, canEdit }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileMeta, setFileMeta] = useState(null); // {name, size, data}
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(null);

  const parseAndDryRun = async (file) => {
    setBusy(true);
    setPlan(null); setApplied(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      setFileMeta({ name: file.name, size: file.size, data });
      const r = await http.post("/mh/import/manahouse-os", { data, mode: "dry_run" });
      setPlan(r.data.plan);
    } catch (e) {
      if (e instanceof SyntaxError) toast.error("That file isn't valid JSON.");
      else toast.error(errText(e, "Couldn't read that import file"));
      setFileMeta(null);
      setPlan(null);
    }
    setBusy(false);
  };

  const apply = async (mode) => {
    if (!fileMeta) return;
    setBusy(true);
    try {
      const r = await http.post("/mh/import/manahouse-os", { data: fileMeta.data, mode });
      setApplied(r.data.applied);
      toast.success(`Imported — ${Object.entries(r.data.applied).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k.replace("_", " ")}`).join(", ") || "no changes"}`);
      if (reload) reload();
    } catch (e) {
      toast.error(errText(e, "Import failed"));
    }
    setBusy(false);
  };

  const reset = () => { setFileMeta(null); setPlan(null); setApplied(null); if (inputRef.current) inputRef.current.value = ""; };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) parseAndDryRun(f);
  };

  return (
    <section className="rounded-3xl border border-white/10 p-5 sm:p-6 bg-white/[0.015] space-y-4 mh-rise" data-testid="import-drop">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Eyebrow>Already have a Manahouse OS?</Eyebrow>
          <h3 className="font-display text-2xl sm:text-3xl mt-1">Bring it in</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">Drop a Local Manahouse OS JSON export here. We'll preview exactly what will change before anything is written. Credentials, API keys and run history are never imported.</p>
        </div>
      </div>

      {!fileMeta && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => canEdit && inputRef.current?.click()}
          data-testid="import-dropzone"
          className={`cursor-pointer rounded-2xl border-2 border-dashed transition-colors p-10 flex flex-col items-center justify-center gap-3 text-center ${dragOver ? "border-[#D4FF3A] bg-[#D4FF3A]/[0.04]" : "border-white/15 hover:border-white/35 bg-black/20"} ${!canEdit ? "opacity-50 cursor-not-allowed" : ""}`}>
          <Upload className="w-7 h-7 text-muted-foreground" />
          <div className="font-display text-lg">{dragOver ? "Drop to preview" : "Drag a Manahouse OS JSON here"}</div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">or click to browse · schema manahouse-os/1</div>
          <input ref={inputRef} type="file" accept="application/json,.json" className="hidden" data-testid="import-file-input"
            onChange={(e) => e.target.files?.[0] && parseAndDryRun(e.target.files[0])} />
        </div>
      )}

      {fileMeta && (
        <div className="rounded-2xl border border-white/10 p-4 bg-black/20 flex items-center gap-3" data-testid="import-file-meta">
          <FileJson className="w-5 h-5 text-[#D4FF3A]" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{fileMeta.name}</div>
            <div className="font-mono text-[10px] text-muted-foreground">{(fileMeta.size / 1024).toFixed(1)} KB · schema {fileMeta.data?.schema_version}</div>
          </div>
          <button onClick={reset} data-testid="import-reset" className="w-8 h-8 rounded-full border border-white/10 grid place-items-center hover:border-white/30"><X className="w-4 h-4" /></button>
        </div>
      )}

      {busy && !plan && (
        <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Working out what would change…</div>
      )}

      {plan && !applied && (
        <div className="space-y-3" data-testid="import-plan">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Stat label="Company facts" value={`${plan.summary.company_facts_to_set} new · ${plan.summary.company_facts_skipped} kept`} tone={plan.summary.company_facts_to_set ? "ok" : null} />
            <Stat label="Dept facts" value={`${plan.summary.dept_facts_to_set} new · ${plan.summary.dept_facts_skipped} kept`} tone={plan.summary.dept_facts_to_set ? "ok" : null} />
            <Stat label="Roles" value={`${plan.summary.roles_to_enable} on · ${plan.summary.roles_to_update} updated · ${plan.summary.roles_skipped} kept`} tone={plan.summary.roles_to_enable + plan.summary.roles_to_update > 0 ? "ok" : null} />
            <Stat label="Workflows" value={`${plan.summary.workflows_to_create} new · ${plan.summary.workflows_to_replace} replaced · ${plan.summary.workflows_skipped} kept`} tone={plan.summary.workflows_to_create > 0 ? "ok" : null} />
          </div>

          {(plan.summary.warnings.unknown_company_keys.length > 0 || plan.summary.warnings.unknown_departments.length > 0 || plan.summary.warnings.unknown_role_slugs.length > 0) && (
            <div className="rounded-xl border border-[#FFB020]/40 bg-[#FFB020]/[0.05] p-3 flex gap-2.5" data-testid="import-warnings">
              <AlertTriangle className="w-4 h-4 text-[#FFB020] shrink-0 mt-0.5" />
              <div className="text-xs text-[#FFB020] space-y-1">
                {plan.summary.warnings.unknown_company_keys.length > 0 && <div>Unknown company keys will be ignored: <code className="font-mono">{plan.summary.warnings.unknown_company_keys.join(", ")}</code></div>}
                {plan.summary.warnings.unknown_departments.length > 0 && <div>Unknown departments will be ignored: <code className="font-mono">{plan.summary.warnings.unknown_departments.join(", ")}</code></div>}
                {plan.summary.warnings.unknown_role_slugs.length > 0 && <div>Unknown role slugs will be ignored: <code className="font-mono">{plan.summary.warnings.unknown_role_slugs.join(", ")}</code></div>}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-white/10 p-3 text-xs text-muted-foreground">
            <span className="text-foreground">Not imported:</span> credentials, API keys, OpenClaw tokens, approvals, runs, receipts.
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
            <button onClick={() => apply("merge")} disabled={busy || !canEdit} data-testid="import-apply-merge"
              className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full bg-[#D4FF3A] text-black text-sm font-mono uppercase tracking-wider disabled:opacity-40">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Merge · keep my edits
            </button>
            <button onClick={() => apply("overwrite")} disabled={busy || !canEdit} data-testid="import-apply-overwrite"
              className="flex-1 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-full border border-white/20 text-sm font-mono uppercase tracking-wider hover:border-white/40 disabled:opacity-40">
              Overwrite · imported wins
            </button>
            <button onClick={reset} data-testid="import-cancel"
              className="h-11 px-4 rounded-full text-sm font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
          <div className="font-mono text-[10px] text-muted-foreground">
            <strong>Merge</strong> fills only what's empty today — your approved edits stay.<br />
            <strong>Overwrite</strong> replaces every touched field with the imported value and swaps name-colliding workflows in place.
          </div>
        </div>
      )}

      {applied && (
        <div className="rounded-2xl border border-[#D4FF3A]/40 bg-[#D4FF3A]/[0.05] p-4 flex gap-3" data-testid="import-done">
          <CheckCircle2 className="w-5 h-5 text-[#D4FF3A] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display text-xl text-[#D4FF3A]">Imported into this workspace</div>
            <div className="text-xs text-muted-foreground mt-1">
              {Object.entries(applied).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(" · ") || "No changes were needed — your workspace already matched the file."}
            </div>
            <button onClick={reset} data-testid="import-another" className="mt-3 h-9 px-3 rounded-full border border-white/15 text-xs font-mono uppercase tracking-wider hover:border-white/40">Import another</button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ImportDrop;
